import { useState } from 'react';
import { api } from '../../lib/api';
import { NOTES_MAX_LENGTH } from '../../../shared/taskNotes';
import { Modal, TrashAction } from '../../components/primitives';
import { Sheet, SheetButton } from '../../components/Sheet';
import { TemplateMobileBody } from '../../components/templateMobileBody';
import { useIsMobile } from '../../lib/useIsMobile';
import type { TaskTemplate, TaskTemplateItem } from './types';

// ── Task-template editor (phase 14) — name + dense item rows ─────────────

export function TemplateModal({
  initial,
  seedName,
  seedItems,
  onClose,
  onSaved,
}: {
  initial: TaskTemplate | null;
  /** "Save these tasks as a template" — prefill a NEW template from a project. */
  seedName?: string;
  seedItems?: TaskTemplateItem[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isMobile = useIsMobile();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [name, setName] = useState(initial?.name ?? seedName ?? '');
  const [items, setItems] = useState<TaskTemplateItem[]>(
    initial
      ? initial.items.map((i) => ({ ...i }))
      : seedItems?.length
        ? seedItems.map((i) => ({ ...i }))
        : isMobile ? [] : [{ title: '', priority: 2, notes: null }],
  );
  // Notes stay collapsed behind a per-item toggle so the editor keeps its
  // one-line-per-task density; items that already carry notes start open.
  const [notesOpen, setNotesOpen] = useState<Set<number>>(
    () => new Set((initial?.items ?? seedItems ?? []).flatMap((item, i) => (item.notes ? [i] : []))),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setItem = (i: number, patch: Partial<TaskTemplateItem>) =>
    setItems((list) => list.map((item, idx) => (idx === i ? { ...item, ...patch } : item)));

  const filled = items.filter((i) => i.title.trim() !== '');
  const valid = name.trim() !== '' && filled.length > 0;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (initial) {
        await api(`/api/admin/task-templates/${initial.id}`, {
          method: 'PATCH',
          json: { name: name.trim(), items: filled },
        });
      } else {
        await api('/api/admin/task-templates', {
          method: 'POST',
          json: { name: name.trim(), items: filled },
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template.');
      setBusy(false);
    }
  };

  if (isMobile) return <>
    <Sheet open variant="full" title={initial ? 'Edit template' : 'New template'} context="Applied tasks land due today" className="settings-template-sheet"
      onClose={() => { if (!busy) onClose(); }}
      leftAction={<SheetButton disabled={busy} onClick={onClose}>Cancel</SheetButton>}
      rightAction={<SheetButton tone="accent" disabled={!valid || busy} onClick={submit}>Save</SheetButton>}
      footer={<button className="btn primary" disabled={!valid || busy} onClick={submit}>{busy ? 'Saving…' : 'Save template →'}</button>}>
      <TemplateMobileBody name={name} setName={setName} items={items} setItems={setItems} busy={busy} onDelete={initial ? () => setConfirmDelete(true) : undefined} />
      {error && !confirmDelete && <p className="error-text settings-confirm" role="alert">{error}</p>}
    </Sheet>
    {confirmDelete && initial && <Sheet open variant="half" title="Delete template?" onClose={() => { if (!busy) setConfirmDelete(false); }}
      leftAction={<SheetButton disabled={busy} onClick={() => setConfirmDelete(false)}>Cancel</SheetButton>}
      rightAction={<SheetButton tone="danger" disabled={busy} onClick={async () => {
        setBusy(true); setError(null);
        try {
          await api(`/api/admin/task-templates/${initial.id}`, { method: 'DELETE' });
          onSaved(); onClose();
        } catch (err) { setError(err instanceof Error ? err.message : 'Could not delete template.'); setBusy(false); }
      }}>Delete</SheetButton>}>
      <div className="settings-confirm">Delete “{name}”? Tasks already applied to projects will stay.
        {error && <p className="error-text" role="alert">{error}</p>}
      </div>
    </Sheet>}
  </>;

  return (
    <Modal
      title={initial ? 'Edit template' : 'New template'}
      context="TASK TEMPLATES"
      width={520}
      onClose={onClose}
      hint="Applied tasks land due today"
      foot={
        <>
          {initial && (
            <span style={{ marginRight: 'auto' }}>
              <TrashAction
                label="Delete template"
                onConfirm={async () => {
                  await api(`/api/admin/task-templates/${initial.id}`, { method: 'DELETE' });
                  onSaved();
                  onClose();
                }}
              />
            </span>
          )}
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit} disabled={!valid || busy}>
            {initial ? 'Save template →' : 'Create template →'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="f-field span-2">
          <span className="f-label">
            Template name <span className="req">*</span>
          </span>
          <input
            className="f-input"
            value={name}
            placeholder="e.g. Shoot prep"
            onChange={(e) => setName(e.target.value)}
            autoFocus={!initial}
          />
        </div>
        <div className="f-field span-2">
          <span className="f-label">Tasks — in order</span>
          {items.map((item, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  className="f-input"
                  style={{ flex: 1 }}
                  value={item.title}
                  placeholder={`Task ${i + 1}…`}
                  onChange={(e) => setItem(i, { title: e.target.value })}
                />
                <button
                  type="button"
                  className="btn"
                  title={item.notes ? 'Notes on this task' : 'Add notes for this task'}
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--fs-eyebrow)',
                    color: item.notes ? 'var(--accent)' : 'var(--text-faint)',
                  }}
                  onClick={() =>
                    setNotesOpen((open) => {
                      const next = new Set(open);
                      if (next.has(i)) next.delete(i);
                      else next.add(i);
                      return next;
                    })
                  }
                >
                  notes
                </button>
                <button
                  type="button"
                  className="btn"
                  title={item.priority === 1 ? 'High priority' : 'Normal priority'}
                  style={item.priority === 1 ? { color: 'var(--danger)' } : { color: 'var(--text-faint)' }}
                  onClick={() => setItem(i, { priority: item.priority === 1 ? 2 : 1 })}
                >
                  ⚑
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ color: 'var(--text-faint)' }}
                  onClick={() => {
                    setItems((list) => list.filter((_, idx) => idx !== i));
                    setNotesOpen(new Set());
                  }}
                >
                  ×
                </button>
              </div>
              {notesOpen.has(i) && (
                <textarea
                  className="f-input notes-textarea"
                  style={{ marginTop: 5, minHeight: 84 }}
                  maxLength={NOTES_MAX_LENGTH}
                  value={item.notes ?? ''}
                  placeholder={'## Kit\n- [ ] Batteries charged'}
                  onChange={(e) => setItem(i, { notes: e.target.value === '' ? null : e.target.value })}
                />
              )}
            </div>
          ))}
          <button
            type="button"
            className="btn"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setItems((list) => [...list, { title: '', priority: 2, notes: null }])}
          >
            + Add task
          </button>
        </div>
        {error && <p className="error-text span-2">{error}</p>}
      </div>
    </Modal>
  );
}
